import { useTranslations } from 'next-intl';
import { type PropsWithChildren, useEffect, useState } from 'react';
import UnlockClipboard from '@/app/assets/imgs/unlock-clipboard.png';
import UnlockCopy from '@/app/assets/imgs/unlock-copy.png';
import { BaseFillLock } from '@/app/components/icons/BaseFillLock';
import { BaseLineClipboard } from '@/app/components/icons/BaseLineClipboard';
import { BaseLineCopy } from '@/app/components/icons/BaseLineCopy';
import { BaseLineInfo } from '@/app/components/icons/BaseLineInfo';
import { BaseLineLoading2 } from '@/app/components/icons/BaseLineLoading2';
import { BaseLineOptimize } from '@/app/components/icons/BaseLineOptimize';
import { BaseLineT2atip } from '@/app/components/icons/BaseLineT2atip';
import { StarIcon } from '@/app/components/icons/StarIcon';
import { SuggestLineReport } from '@/app/components/icons/SuggestLineReport';
import { CustomSwitch } from '@/app/components/shared/CustomSwitch';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { ImageWithPlaceholder } from '@/app/components/shared/ImageWithPlaceholder';
import { TipsCarousel } from '@/app/components/shared/TipsCarousel';
import { Button } from '@/app/components/ui/button';
import {
  HoverCard,
  HoverCardArrow,
  HoverCardContent,
  HoverCardPortal,
  HoverCardTrigger,
} from '@/app/components/ui/hover-card';
import { toast } from '@/app/components/ui/use-toast';
import { SITE } from '@/app/constants';
import { useCopy, useDevice, useFetch, useGlobalModal } from '@/app/hooks';
import { useStore, useStoreApi } from '@/app/providers/store';
import type { FormParams } from '@/app/stores/slices/task-form';
import type {
  ElementModality,
  FormTabType,
  MentionItem,
  RecaptionPrompt,
  ReferencePrompt,
  TextPromptWithInputItem,
} from '@/app/types';
import {
  cn,
  convertForm,
  convertProTextPromptToSimple,
  getDefaultRecaptionPrompt,
  getPromptMaxLength,
  reportEvent,
} from '@/app/utils';
import { useLocale } from '@/i18n/routing';
import { T2A_TIPS_INTERVAL } from '../../(tools)/ai-sound-effects/constant';
import { tasksRecaption } from '../../apis';
import { ClipboardPanel, PasteMode } from './components/ClipboardPanel';
import { InfoTip } from './components/InfoTip';
import { ProTextarea } from './components/ProTextarea';
import { EXAMPLE_PROMPT } from './components/ProTextarea/constants';
import { AUTO_CLOSE_AFTER, HD_LABEL, NONE_LABEL, SPLIT_LABEL, TIPS_TEXT } from './constants';
import { convertSimpleToProTextPrompt } from './utils';

interface FormTextareaProps<T extends string | TextPromptWithInputItem = string> {
  id?: string;
  className?: string;
  taskType: Exclude<FormTabType, 'scene'>;
  basicTextPrompt: T;
  onCloseProMode?: (value: T) => void;
  onClearPrompt?: () => void;
  onOpenPromptLibraryPopover?: () => void;
  allowMention?: boolean;
  isWarningTipVisible?: boolean;
  setIsWarningTipVisible?: (value: boolean) => void;
  onBeforeAddMention?: (mentionItem: MentionItem) => boolean;
  onBeforeDeleteMention?: (mentionsIdsToDelete: string[], updatedContent: TextPromptWithInputItem) => boolean;
  getExtraItems?: () => ReferencePrompt[];
  onOpenPlanDialog?: () => void;
  /** 是否隐藏 专业模式 入口，默认不隐藏 */
  hideProMode?: boolean;
  disabledProMode?: boolean;
  /** 允许的 modality 筛选 */
  allowedModalities?: Array<ElementModality>;
}

export const FormTextarea = <T extends string | TextPromptWithInputItem = string>({
  id,
  className,
  taskType,
  basicTextPrompt,
  children,
  onCloseProMode,
  onClearPrompt,
  onOpenPromptLibraryPopover,
  allowMention = false,
  isWarningTipVisible,
  setIsWarningTipVisible,
  onBeforeAddMention,
  onBeforeDeleteMention,
  getExtraItems,
  onOpenPlanDialog,
  hideProMode = false,
  disabledProMode = false,
  allowedModalities,
}: PropsWithChildren<FormTextareaProps<T>>) => {
  const { modelVersion, formParams, updateFormParams, trialsProMode, isFreeUser } = useStore((state) => ({
    modelVersion: state[`${taskType}FormParams`].modelVersion,
    formParams: state[`${taskType}FormParams`],
    updateFormParams: state.updateFormParams,
    trialsProMode: state.trialsProMode,
    isFreeUser: state.userInfo?.subs_plan === 'free',
  }));
  const { open: openConfirmDialog } = useGlobalModal('confirmDialog');

  const { isProMode, recaptionPrompt } = formParams;
  const updateIsProMode = (isProMode: boolean) => {
    updateFormParams(taskType, 'isProMode', isProMode);
  };

  const store = useStoreApi();

  const updateRecaptionPrompt = (newRecaptionPrompt: Partial<RecaptionPrompt>, partial = false) => {
    const defaultRecaptionPrompt = getDefaultRecaptionPrompt(transProMode);
    if (!partial) {
      updateFormParams(taskType, 'recaptionPrompt', { ...defaultRecaptionPrompt, ...newRecaptionPrompt });
      return;
    }

    // 局部更新
    const draft: RecaptionPrompt = {
      ...defaultRecaptionPrompt,
      ...store.getState()[`${taskType}FormParams`].recaptionPrompt,
      ...newRecaptionPrompt,
    };
    updateFormParams(taskType, 'recaptionPrompt', draft);
  };

  const { isMobile } = useDevice();
  const shouldUpgrade = isFreeUser && !trialsProMode?.remaining; // 显示 ProMode 入口时，shouldUpgrade 的值才会有意义，不用担心加载 trialsProMode 过程中误判为无试用次数

  const t = useTranslations();
  const transProMode = useTranslations('create.proMode');
  const locale = useLocale();
  const [isTipsVisible, setIsTipsVisible] = useState(true);
  const [isClipboardVisible, setIsClipboardVisible] = useState(false);
  const [isUndoVisible, setIsUndoVisible] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [autoTranslate, setAutoTranslate] = useState(locale !== 'en');
  const { copy } = useCopy();

  useEffect(() => {
    setIsClient(true);
  }, []);

  // 强制关闭 Pro mode 的核心逻辑
  const forceCloseProMode = () => {
    updateIsProMode(false);

    if (recaptionPrompt) {
      const newSimplePrompt = convertProTextPromptToSimple(recaptionPrompt, {
        t: transProMode,
        outputFormat: typeof basicTextPrompt === 'string' ? 'text' : 'with-reference',
      });
      onCloseProMode?.(newSimplePrompt as T);
      updateRecaptionPrompt({});
      setIsUndoVisible(false);
    }
  };

  // 在手机端强制关闭 Pro mode
  useEffect(() => {
    if (!isProMode) {
      return;
    }

    if (isMobile) {
      forceCloseProMode();
    }
  }, [isMobile, isProMode]);

  // q2 模型不支持专业模式
  useEffect(() => {
    if ((disabledProMode || hideProMode) && isProMode) {
      forceCloseProMode();
    }
  }, [isProMode, disabledProMode, hideProMode]);

  const toggleProMode = async (value: boolean) => {
    if (isMobile) {
      openConfirmDialog?.({
        content: t('create.proMode.mobileTip'),
        primaryButtonText: t('popup.dialog.error.confirm'),
      });
      return;
    }

    updateIsProMode(value);

    // 重置提示
    setIsWarningTipVisible?.(false);

    if (shouldUpgrade) {
      updateRecaptionPrompt({
        subject: [{ type: 'text', text: EXAMPLE_PROMPT[SITE][locale]?.subject || '' }],
        environment: [{ type: 'text', text: EXAMPLE_PROMPT[SITE][locale]?.environment || '' }],
      });
      return;
    }

    // 打开 pro mode
    if (value && !isProMode) {
      if (basicTextPrompt.length) {
        const params = { ...formParams };
        if (typeof basicTextPrompt === 'string') {
          params.textPrompt = basicTextPrompt;
        } else {
          params.textPromptWithReference = basicTextPrompt;
        }
        const { data, error } = await handleOptimize(params);
        // 如果 optimize 失败，兜底逻辑为直接 Parse，剩余的填入 subject 末尾
        if (!data?.content || error) {
          const prompt = convertSimpleToProTextPrompt(basicTextPrompt, [], transProMode);
          updateRecaptionPrompt(prompt);
        } else {
          // optimize 成功，允许 undo
          setIsUndoVisible(true);
        }
      } else {
        // basicTextPrompt 为空时，直接使用默认值
        updateRecaptionPrompt({});

        // 由于内容为空，打开剪贴板
        setIsClipboardVisible(true);
      }
    }

    // 关闭 pro mode
    if (!value && isProMode) {
      forceCloseProMode();
    }
  };

  const optimizeLanguage = autoTranslate ? locale : 'en';

  const { runAsync: handleOptimize, loading: loadingOptimize } = useFetch(
    (params?: FormParams) =>
      tasksRecaption({
        body: {
          ...convertForm({ taskType, formParams: params ?? formParams, modelVersion }),
          target_language: optimizeLanguage,
        },
      }),
    {
      manual: true,
      onSuccess: ({ data, error }) => {
        if (!data?.content || error) {
          // 如果审核失败，打开弹窗
          if (error?.reason === 'AuditSubmitIllegal' || error?.reason === 'TaskPromptPolicyViolation') {
            const auditId = error?.metadata?.audit_id;
            const message = t('popup.dialog.error.checkFailed.content');
            openConfirmDialog?.({
              title: t('popup.dialog.error.checkFailed.title'),
              content: auditId ? `${message}\n(ID: ${auditId})` : message,
              primaryButtonText: t('popup.dialog.error.confirm'),
            });
          }

          // 如果失败原因是优化次数达到当日上限，出 toast 提示
          if (error?.reason === 'RecaptionOverLimit') {
            toast({ message: t('create.proMode.RecaptionOverLimit'), type: 'warning' });
          }
          return;
        }

        let msg: {
          Subject: string;
          Environment: string;
          Style: string;
          'Motion Level': string;
          'Shot Size': string;
          'Focal Length': string;
          Composition: string;
          'Camera Angle': string;
          'Special Filming Technique': string;
          'Camera Movement': string;
        };
        try {
          msg = JSON.parse(data.content);
        } catch {
          return;
        }

        const splitOrEmpty = (str: string | undefined | null): string[] | undefined => {
          return str && str.length > 0 && str !== NONE_LABEL[SITE][optimizeLanguage]
            ? str.replace(HD_LABEL[SITE][optimizeLanguage] ?? '', '').split(SPLIT_LABEL[SITE][optimizeLanguage] ?? '')
            : undefined;
        };
        const { style, motionLevel } = getDefaultRecaptionPrompt(transProMode);

        updateRecaptionPrompt({
          subject: [{ type: 'text', text: msg.Subject ?? '' }],
          environment: [{ type: 'text', text: msg.Environment ?? '' }],
          style: splitOrEmpty(msg.Style) ?? style,
          motionLevel: splitOrEmpty(msg['Motion Level']) ?? motionLevel,
          shotSize: splitOrEmpty(msg['Shot Size']),
          focalLength: splitOrEmpty(msg['Focal Length']),
          composition: splitOrEmpty(msg.Composition),
          cameraAngle: splitOrEmpty(msg['Camera Angle']),
          specialFilmingTechnique: splitOrEmpty(msg['Special Filming Technique']),
          cameraMovement: splitOrEmpty(msg['Camera Movement']),
        });
      },
    },
  );

  const handleCopy = () => {
    const text =
      isProMode && recaptionPrompt
        ? convertProTextPromptToSimple(recaptionPrompt, { t: transProMode, outputFormat: 'text' })
        : basicTextPrompt;
    copy(text as string);
  };

  const handlePaste = (text: string, mode: PasteMode) => {
    if (mode === PasteMode.Recognition) {
      // 直接 parse 文本
      const prompt = convertSimpleToProTextPrompt(text, [], transProMode);
      updateRecaptionPrompt(prompt);
    } else {
      reportEvent('element_click', {
        element_name: 'pro_mode_optimize',
        element_click_position: 'pro_mode_board',
      });
      handleOptimize({
        ...formParams,
        // 当作简单模式取 textPrompt（主体资产化除外）
        textPrompt: text,

        // 主体资产化会取 textPromptWithReference
        textPromptWithReference: [
          {
            type: 'text',
            text,
          },
        ],
        isProMode: false,
      });
    }
  };

  const handleClickOptimizeButton = async (clickPosition: 'pro_mode_button' | 'pro_mode_reminder') => {
    if (loadingOptimize) {
      return;
    }
    handleOptimize();
    setIsWarningTipVisible?.(false);
    reportEvent('element_click', {
      element_name: 'pro_mode_optimize',
      element_click_position: clickPosition,
    });
  };

  const handleUndo = () => {
    // 打开 pro mode 自动根据 basicTextPrompt optimize 后 undo（之前没有 pro 格式的 prompt 和 recaptionSettings）
    const prompt = convertSimpleToProTextPrompt(basicTextPrompt, [], transProMode);
    updateRecaptionPrompt(prompt);
    setIsUndoVisible(false);
  };

  const optimizeButton = (
    <Button
      className={cn(
        'relative h-9 min-w-[80px] gap-2 rounded-10 border border-system-white36 px-4 text-xs lg:px-2',
        loadingOptimize && 'text-system-white36',
      )}
      icon={
        loadingOptimize ? (
          <BaseLineLoading2 className="animate-spin text-base" />
        ) : (
          <BaseLineOptimize className="text-base" />
        )
      }
      onClick={shouldUpgrade ? onOpenPlanDialog : () => handleClickOptimizeButton('pro_mode_button')}
    >
      <span className="-z-10 absolute inset-0 overflow-hidden rounded-8 bg-black shadow-[inset_0_0_24px_0_rgba(138,136,255,0.48)]">
        <span className="-top-4 -left-2 absolute size-[66px] rounded-full bg-system-blue02 opacity-80 blur-2xl" />
      </span>
      {t('create.proMode.optimize')}
      {shouldUpgrade && (
        <span className="-right-3 -top-2.5 absolute scale-[.8] rounded-8 bg-system-blue02 px-2 py-0.5 text-xs">
          {t('create.createForm.badge.upgrade')}
        </span>
      )}
    </Button>
  );

  const optimizeButtonWithTranslation = (
    <HoverCard openDelay={0} closeDelay={100}>
      <HoverCardTrigger asChild>{optimizeButton}</HoverCardTrigger>
      {isClient && (
        <HoverCardPortal container={document.body}>
          <HoverCardContent
            className="flex w-fit gap-2 bg-system-bg02 p-1.5 pl-3"
            align="center"
            sideOffset={2}
            side="top"
          >
            <CustomSwitch
              checked={autoTranslate}
              onChange={setAutoTranslate}
              label={t('create.proMode.translation.name')}
            />
            <div className="flex items-center gap-1 rounded-6 bg-system-white10 px-2 py-1 text-xs">
              <span>{t('create.proMode.translation.from')}</span>
              <span className="text-system-white48">→</span>
              <span>{t('create.proMode.translation.to')}</span>
            </div>
            <HoverCardArrow />
          </HoverCardContent>
        </HoverCardPortal>
      )}
    </HoverCard>
  );

  return (
    <div
      id={id}
      className={cn(
        'relative flex min-h-[140px] w-full flex-1 flex-col rounded-10 bg-[rgba(0,0,0,0.32)] p-3 text-sm backdrop-blur-[30px] sm:text-sm lg:text-xs',
        className,
      )}
    >
      {isProMode ? (
        <>
          {isUndoVisible && (
            <InfoTip
              message={t.rich('create.proMode.autoOptimizedUndo', {
                button: (chunks) => (
                  <span className="ms-1 me-1 cursor-pointer text-system-blue02" onClick={handleUndo}>
                    {chunks}
                  </span>
                ),
              })}
              prefixIcon={<BaseLineInfo className="text-base text-system-white/40" />}
              onClose={() => setIsUndoVisible(false)}
              autoCloseAfter={AUTO_CLOSE_AFTER}
            />
          )}
          {isWarningTipVisible && (
            <InfoTip
              message={
                <span>{t(allowMention ? 'create.proMode.referenceChangedTip' : 'create.proMode.imageChangedTip')}</span>
              }
              prefixIcon={<SuggestLineReport className="shrink-0 self-start text-base text-system-white" />}
              action={
                <Button
                  className="h-fit rounded-8 bg-system-blue02/20 px-2 py-1 text-system-blue02 text-xs leading-[140%] hover:bg-system-blue02/40"
                  size="sm"
                  onClick={() => handleClickOptimizeButton('pro_mode_reminder')}
                >
                  {t('create.proMode.optimize')}
                </Button>
              }
              onClose={() => setIsWarningTipVisible?.(false)}
            />
          )}
          <ProTextarea
            maxLength={getPromptMaxLength(modelVersion)}
            recaptionPrompt={recaptionPrompt}
            updateRecaptionPrompt={updateRecaptionPrompt}
            loading={loadingOptimize}
            allowMention={allowMention}
            onBeforeAddMention={onBeforeAddMention}
            onBeforeDeleteMention={onBeforeDeleteMention}
            shouldUpgrade={shouldUpgrade}
            onClear={() => {
              onClearPrompt?.();
              setIsWarningTipVisible?.(false);
            }}
            onOpenPromptLibraryPopover={onOpenPromptLibraryPopover}
            getExtraItems={getExtraItems}
            onOpenPlanDialog={onOpenPlanDialog}
            allowedModalities={allowedModalities}
          />
          <TipsCarousel
            className="mb-1"
            isTipsVisible={isTipsVisible}
            tipsData={TIPS_TEXT[SITE][locale] ?? []}
            intervalDuration={T2A_TIPS_INTERVAL * 1000}
          />
          <div className="mt-2 flex justify-between">
            <div />
            {locale === 'en' || shouldUpgrade ? (
              <CustomTooltip content={shouldUpgrade ? t('create.proMode.unlockOptimize') : ''}>
                {optimizeButton}
              </CustomTooltip>
            ) : (
              optimizeButtonWithTranslation
            )}
          </div>

          {/* 剪贴板 */}
          <ClipboardPanel
            isVisible={isClipboardVisible}
            onClose={() => setIsClipboardVisible(false)}
            onPaste={handlePaste}
            maxLength={getPromptMaxLength(modelVersion)}
          />
        </>
      ) : (
        children
      )}
      <div className="absolute bottom-3 left-3 flex select-none items-center gap-1 lg:gap-0">
        {!hideProMode && (
          <>
            <CustomSwitch
              className={!isMobile ? 'sm:hidden' : ''}
              checked={isProMode}
              onChange={toggleProMode}
              label={t('create.proMode.proText')}
              tooltip={
                disabledProMode
                  ? t('create.proMode.proDisabled')
                  : isProMode
                    ? t('create.proMode.basicTip')
                    : t('create.proMode.proTip')
              }
              disabled={disabledProMode}
            />
            {!disabledProMode && isFreeUser && Boolean(trialsProMode?.remaining) && (
              <CustomTooltip content={t('create.proMode.freeTrialTip')}>
                <span className="relative ms-2 pr-2.5 sm:hidden lg:pr-1">
                  <span className="bg-gradient-to-l from-[#479AFF] to-[#A5C7FF] bg-clip-text font-medium text-transparent text-xs">
                    {t('create.proMode.freeTrials', {
                      count: trialsProMode?.remaining,
                      s: (trialsProMode?.remaining ?? 0) > 1 ? 's' : '',
                    })}
                  </span>
                  <StarIcon className="-top-1 -right-0.5 absolute text-sm lg:hidden" />
                </span>
              </CustomTooltip>
            )}
          </>
        )}
        {isProMode && (
          <>
            <CustomTooltip content={t('text2audio.timestampTextArea.tips')}>
              <Button
                className="size-8 border-none p-0 font-normal"
                size="sm"
                onClick={() => setIsTipsVisible(!isTipsVisible)}
              >
                <BaseLineT2atip className="text-lg" isActive={isTipsVisible} />
              </Button>
            </CustomTooltip>
            {(recaptionPrompt || shouldUpgrade) && (
              <CustomTooltip
                contentClassName="text-center"
                content={
                  shouldUpgrade ? (
                    <>
                      <ImageWithPlaceholder
                        src={UnlockCopy.src}
                        alt="unlock copy"
                        className="mb-1 h-[140px] w-[240px] rounded-4"
                      />
                      <span className="text-xs">
                        {t.rich('create.proMode.unlockCopy', {
                          upgrade: (chunks) => (
                            <span className="cursor-pointer text-system-blue02" onClick={onOpenPlanDialog}>
                              {chunks}
                            </span>
                          ),
                        })}
                      </span>
                    </>
                  ) : (
                    t('create.proMode.copy')
                  )
                }
              >
                <Button
                  className={cn(
                    'relative size-8 p-0 font-normal',
                    shouldUpgrade
                      ? 'border border-system-white08 bg-system-black36 text-system-white32 lg:border-none'
                      : 'border-none',
                  )}
                  size="sm"
                  onClick={handleCopy}
                  disabled={shouldUpgrade}
                >
                  <BaseLineCopy className={cn('text-base', shouldUpgrade && 'text-system-white opacity-[.32]')} />
                  {shouldUpgrade && (
                    <BaseFillLock className="-right-1 -bottom-0.5 lg:-right-0.5 absolute text-xs lg:bottom-0" />
                  )}
                </Button>
              </CustomTooltip>
            )}
            <CustomTooltip
              contentClassName="text-center"
              content={
                shouldUpgrade ? (
                  <>
                    <ImageWithPlaceholder
                      src={UnlockClipboard.src}
                      alt="unlock clipboard"
                      className="mb-1 h-[140px] w-[240px] rounded-4"
                    />
                    <span className="text-xs">
                      {t.rich('create.proMode.unlockPaste', {
                        upgrade: (chunks) => (
                          <span className="cursor-pointer text-system-blue02" onClick={onOpenPlanDialog}>
                            {chunks}
                          </span>
                        ),
                      })}
                    </span>
                  </>
                ) : (
                  t('create.proMode.clipboard.title')
                )
              }
            >
              <Button
                className={cn(
                  'relative size-8 p-0 font-normal',
                  shouldUpgrade
                    ? 'border border-system-white08 bg-system-black36 text-system-white32 lg:border-none'
                    : 'border-none',
                )}
                size="sm"
                onClick={() => setIsClipboardVisible(!isClipboardVisible)}
                disabled={shouldUpgrade}
              >
                <BaseLineClipboard className={cn('text-base', shouldUpgrade && 'text-system-white opacity-[.32]')} />
                {shouldUpgrade && (
                  <BaseFillLock className="-right-1 -bottom-0.5 lg:-right-0.5 absolute text-xs lg:bottom-0" />
                )}
              </Button>
            </CustomTooltip>
          </>
        )}
      </div>
    </div>
  );
};
